import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState } from 'store/auth/userSlice'
import { apiSignIn, apiSignOut, apiSignUp, apiInviteSignUp, apiAddToProject } from 'services/AuthService'
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import getFirstPermittedRoute from 'utils/getFirstPermittedRoute'

function useAuth() {
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const { token, signedIn } = useSelector((state) => state.auth.session)

    // Son projeyi otomatik seçip yönlendiren yardımcı fonksiyon
    const autoSelectProject = async (user) => {
        try {
            const [resp, projectResp] = await Promise.all([
                fetch(appConfig.APIURL + 'project/set_project.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ project_id: user.project_id, user_id: user.user_id }),
                }),
                fetch(appConfig.APIURL + `project/get_projects.php?project_id=${user.project_id}`),
            ])
            const data = await resp.json()
            const projectData = await projectResp.json().catch(() => null)
            const projectMeta = Array.isArray(projectData) ? projectData[0] : projectData

            const projectAuthority = data.rank ? [data.rank] : []
            const projectPermissions = (data.user_permissions && data.user_permissions !== 'null')
                ? data.user_permissions
                : []

            const updatedUserInfo = {
                user_photo: user.user_photo,
                user_name: user.user_name,
                user_fullname: user.user_fullname,
                user_id: user.user_id,
                project_title: user.project_title,
                project_id: user.project_id,
                authority: projectAuthority,
                user_mail: user.user_mail,
                permissions: projectPermissions,
                user_subscribe: user.user_subscribe,
                date_format: projectMeta?.date_format || '',
            }

            dispatch(setUser(updatedUserInfo))
            navigate(getFirstPermittedRoute(updatedUserInfo))
        } catch (error) {
            console.error('Otomatik proje seçimi hatası:', error)
            navigate(appConfig.selectProject)
        }
    }

    const signIn = async (values) => {
        try {
            const resp = await apiSignIn(values)
            if (resp.data && resp.data.user) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                dispatch(
                    setUser(
                        resp.data.user || {
                            avatar: resp?.data?.user?.user_photo,
                            userName: resp?.data?.user?.user_name,
                            userFullName: resp?.data?.user?.user_fullname,
                            project_title: resp?.data?.user?.project_title,
                            project_id: resp?.data?.user?.project_id,
                            authority: [resp?.data?.user?.user_rank],
                            email: resp?.data?.user?.user_mail,
                            permissions: [resp?.data?.user?.permissions]
                        }
                    )
                )

                // Pending invite kontrolü - localStorage'da bekleyen davet var mı?
                const pendingInviteStr = localStorage.getItem('pendingInvite')
                if (pendingInviteStr) {
                    try {
                        const pendingInvite = JSON.parse(pendingInviteStr)
                        // Kullanıcı email'i eşleşiyor mu kontrol et (case-insensitive)
                        if (pendingInvite.email?.toLowerCase() === resp.data.user.user_mail?.toLowerCase()) {
                            // Projeye ekle
                            try {
                                const addResult = await apiAddToProject({
                                    user_id: resp.data.user.user_id,
                                    project_id: pendingInvite.project_id,
                                    group: pendingInvite.group
                                })

                                // localStorage'ı temizle (başarılı veya başarısız)
                                localStorage.removeItem('pendingInvite')

                                // Projeye ekleme başarılıysa veya zaten projede ise
                                if (addResult?.data?.status === 'success') {
                                    navigate(appConfig.selectProject)
                                    return {
                                        status: 'success',
                                        message: 'Giriş yapıldı ve projeye dahil edildiniz.',
                                    }
                                }
                            } catch (addError) {
                                console.error('Projeye ekleme hatası:', addError)
                                // 409 Conflict = Kullanıcı zaten projede, bu durumda da selectProject'e git
                                if (addError?.response?.status === 409) {
                                    localStorage.removeItem('pendingInvite')
                                    navigate(appConfig.selectProject)
                                    return {
                                        status: 'success',
                                        message: 'Giriş yapıldı. Zaten bu projeye dahilsiniz.',
                                    }
                                }
                                localStorage.removeItem('pendingInvite')
                            }
                        } else {
                            // Email eşleşmedi, localStorage'ı temizle
                            console.warn('Pending invite email eşleşmedi:', pendingInvite.email, resp.data.user.user_mail)
                            localStorage.removeItem('pendingInvite')
                        }
                    } catch (inviteError) {
                        console.error('Pending invite işlemi sırasında hata:', inviteError)
                        localStorage.removeItem('pendingInvite')
                    }
                }

                if(resp.data.user.authority=='SUPERADMIN'){
                    const redirectUrl = query.get(REDIRECT_URL_KEY)
                    navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath)
                } else if(resp.data.user.project_id){
                    // Kullanıcının daha önce seçtiği bir projesi varsa otomatik giriş yap
                    await autoSelectProject(resp.data.user)
                } else{
                    navigate(appConfig.selectProject)
                }

                return {
                    status: 'success',
                    message: 'Giriş yapılıyor.',
                }
            } else {
                // Servisten başarılı response geldi ama user bilgisi yok (hatalı giriş)
                return {
                    status: 'failed',
                    message: resp.data?.message || 'Giriş başarısız.',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const signUp = async (values) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data && resp.data.user) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                dispatch(
                    setUser(
                        resp.data.user || {
                            avatar: resp?.data?.user?.user_photo,
                            userName: resp?.data?.user?.user_name,
                            userFullName: resp?.data?.user?.user_fullname,
                            project_title: resp?.data?.user?.project_title,
                            project_id: resp?.data?.user?.project_id,
                            authority: [resp?.data?.user?.authority],
                            email: resp?.data?.user?.user_mail,
                            permissions: [resp?.data?.user?.permissions]
                        }
                    )
                )

                if(resp?.data?.user?.authority=='SUPERADMIN'){
                    const redirectUrl = query.get(REDIRECT_URL_KEY)
                    navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath)
                } else{
                    const redirectUrl = query.get(REDIRECT_URL_KEY)
                    navigate(redirectUrl ? redirectUrl : appConfig.selectProject)
                }

                return {
                    status: 'success',
                    message: 'Giriş yapılıyor.',
                }
            } else {
                // Servisten başarılı response geldi ama user bilgisi yok (hatalı kayıt)
                return {
                    status: 'failed',
                    message: resp.data?.message || 'Kayıt başarısız.',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const inviteSignUp = async (values) => {
        try {
            const resp = await apiInviteSignUp(values)
            if (resp.data && resp.data.user) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))

                const userData = resp.data.user

                // Authority'yi her zaman array olarak normalize et
                let authority = userData.authority
                if (authority && !Array.isArray(authority)) {
                    authority = [authority]
                } else if (!authority) {
                    authority = []
                }

                // Permissions normalize
                const permissions = (userData.permissions && userData.permissions !== 'null')
                    ? userData.permissions
                    : []

                const normalizedUser = {
                    ...userData,
                    authority,
                    permissions,
                }

                dispatch(setUser(normalizedUser))

                // Davet ile kayıt olan kullanıcı zaten projeye eklendi,
                // autoSelectProject ile doğru yetkileri yükle
                if (userData.project_id && userData.user_id) {
                    await autoSelectProject(normalizedUser)
                } else {
                    navigate(getFirstPermittedRoute(normalizedUser))
                }

                return {
                    status: 'success',
                    message: 'Kayıt başarılı, yönlendiriliyorsunuz.',
                }
            } else {
                return {
                    status: 'failed',
                    message: resp.data?.message || 'Kayıt başarısız.',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(setUser(initialState))
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        await apiSignOut()
        handleSignOut()
    }

    return {
        authenticated: token && signedIn,
        signIn,
        signUp,
        inviteSignUp,
        signOut,
    }
}

export default useAuth
